import AddRounded from "@mui/icons-material/AddRounded";
import ChevronRight from "@mui/icons-material/ChevronRight";
import EditOutlined from "@mui/icons-material/EditOutlined";
import KeyboardReturnOutlined from "@mui/icons-material/KeyboardReturnOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import SearchOffOutlined from "@mui/icons-material/SearchOffOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material";
import { ModuleRegistry } from "ag-grid-community";
import { RichSelectModule } from "ag-grid-enterprise";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";

import {
  loanDrilldownClear,
  loanDrilldownSet,
} from "../../redux/actions/loanDrilldownActions";

import Flx from "../../components/layout/Flx";
import ScreenHeader from "../../components/layout/ScreenHeader";
import Htag from "../../components/typography/Htag";

import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import size from "lodash/size";
import toString from "lodash/toString";
import RffLoanDataGroup from "../../components/finalForm/shared/RffLoanDataGroup";

import UpdateLoanDataModal from "../../components/common/Loan/UpdateLoanDataModal";
import RffDisplayField from "../../components/finalForm/inputs/RffDisplayField";
import RffSelectMultipleUserEmailField from "../../components/finalForm/inputs/RffSelectMultipleUserEmailField";
import RffForm from "../../components/finalForm/RffForm";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import ScreenContent from "../../components/layout/ScreenContent";
import BasicModal from "../../components/modals/BasicModal";
import Txt from "../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import {
  navSidebarClose,
  navSidebarOpen,
} from "../../redux/actions/navSidebarActions";
import { sidebarClear } from "../../redux/actions/sidebarActions";
import AddToLoanDropdownButton from "./buttons/AddToLoanDropdownButton";
import SalesforceIntegrationButton from "./buttons/SalesforceIntegrationButton";
import SendLoanApplicationsDropdownButton from "./buttons/SendLoanApplicationsDropdownButton";
import UploadToLoanDropdownButton from "./buttons/UploadToLoanDropdownButton";
import LoanDrilldownRouterTabSwitcher from "./components/LoanDrilldownRouterTabSwitcher";
import LoanDrilldownScreenSidebar from "./LoanDrilldownScreenSidebar";
import LoanDrilldownTotalsLeftSidebar from "./LoanDrilldownTotalsLeftSidebar";
import LoanSizerUploader from "./shared/LoanSizerUploader";
import LoanDrilldownAppraisalsTab from "./tabs/LoanDrilldownAppraisalsTab/LoanDrilldownAppraisalsTab";
import LoanDrilldownBorrowersTab from "./tabs/LoanDrilldownBorrowersTab/LoanDrilldownBorrowersTab";
import LoanDrilldownChecklistTab from "./tabs/LoanDrilldownChecklistTab/LoanDrilldownChecklistTab";
import LoanDrilldownDetailsTab from "./tabs/LoanDrilldownDetailsTab/LoanDrilldownDetailsTab";
import LoanDrilldownEntityTab from "./tabs/LoanDrilldownEntityTab/LoanDrilldownEntityTab";
import LoanDrilldownExceptionsTab from "./tabs/LoanDrilldownExceptionsTab/LoanDrilldownExceptionsTab";
import LoanDrilldownFilesTab from "./tabs/LoanDrilldownFilesTab/LoanDrilldownFilesTab";
import LoanDrilldownPropertiesTab from "./tabs/LoanDrilldownPropertiesTab/LoanDrilldownPropertiesTab";
import LoanDrilldownSizerTab from "./tabs/LoanDrilldownSizerTab";
import LoanDrilldownSummaryTab from "./tabs/LoanDrilldownSummaryTab";
import { AddAlertOutlined, ErrorOutline } from "@mui/icons-material";
import { red } from "@mui/material/colors";

// Register AG Grid modules
ModuleRegistry.registerModules([RichSelectModule]);
const initialLoanData = {
  _id: "67f44f69e685b472c479f7dd",
};

/** =====================
 * MAIN SCREEN COMPONENT
 * ===================== */
const LoanDrilldownScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: loanNumber } = useParams();
  const { getLoanById } = useUnderwritingHook();
  const { getUserPipeline } = usePipelineHook();

  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const [loanIdMap, setLoanIdMap] = useLocalStorageState("loanIdMap", {
    defaultValue: {},
  });

  const updateLoanIdMap = (loanNumber, loanId) => {
    setLoanIdMap((prevMap) => ({
      ...prevMap,
      [loanNumber]: loanId,
    }));
  };

  const fetchLoanById = (loanId) => {
    getLoanById({
      loanId,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        if (data.loanNumber && loanId) {
          updateLoanIdMap(data.loanNumber, loanId);
        }
      },
      onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  const findLoanInPipelineAndFetch = (pipeline) => {
    const foundLoan = pipeline.find(
      (l) => toString(l.loanNumber) === toString(loanNumber)
    );

    if (isString(foundLoan?._id)) {
      fetchLoanById(foundLoan._id);
    } else {
      dispatch(loanDrilldownSet({}));
    }
  };

  useEffect(() => {
    dispatch(navSidebarClose());
    if (loanNumber === "dev") {
      dispatch(loanDrilldownSet(initialLoanData));
      return;
    }
    if (!isNil(loanDrilldown?._id)) {
      return;
    }

    const storedLoanId = loanIdMap[loanNumber];

    if (isString(storedLoanId)) {
      fetchLoanById(storedLoanId);
    } else {
      if (isArray(loanPipeline) && loanPipeline.length > 0) {
        findLoanInPipelineAndFetch(loanPipeline);
      } else {
        getUserPipeline({
          onSuccessFn: (pipeline) => {
            dispatch(loanPipelineSet(pipeline));
            findLoanInPipelineAndFetch(pipeline);
          },
          onFailFn: () => dispatch(loanDrilldownSet({})),
        });
      }
    }

    return () => {
      dispatch(navSidebarOpen());
    };
  }, [loanNumber]);

  if (isNil(loanDrilldown)) {
    return (
      <Flx center sx={{ p: 4, height: "100vh" }}>
        <CircularProgress />
      </Flx>
    );
  }

  if (isObject(loanDrilldown) && isEmpty(loanDrilldown)) {
    return (
      <Flx gap={1} column center sx={{ p: 4, height: "100vh" }}>
        <SearchOffOutlined fontSize="large" />
        <Htag h1>No Loan Found</Htag>
        <Txt>We encountered a problem trying to retrieve your loan.</Txt>
        <Flx sx={{ mt: 1 }}>
          <Button
            startIcon={<KeyboardReturnOutlined />}
            onClick={() => navigate("/loans")}
          >
            Return to Pipeline
          </Button>
        </Flx>
      </Flx>
    );
  }

  return <LoanDrilldownContent navigate={navigate} />;
};

/** =========================
 * CONTENT RENDERER
 * ========================= */
const LoanDrilldownContent = memo(({ navigate }) => {
  const [showTotals, setShowTotals] = useState(false);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { type } = useSelector((state) => state.sidebar);
  const baseUrl = useMemo(
    () => `/loan/${loanDrilldown?.loanNumber}`,
    [loanDrilldown]
  );
  const dispatch = useDispatch();
  const toggleShowTotals = useCallback(() => {
    setShowTotals((s) => !s);
  }, []);
  const closeTotals = useCallback(() => {
    setShowTotals(false);
  }, []);
  return (
    <Flx
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        // background: "red",
        background: "#f8fafc",
        overflow: "hidden",
        // background: "#f5f8fa",
      }}
    >
      <LoanDrilldownTotalsLeftSidebar
        show={showTotals}
        onClose={closeTotals}
        onOpen={toggleShowTotals}
      />
      <Box
        id="overflow-root"
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          height: "100%",
          // "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f5f8fa" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
          },
          scrollbarColor: "#bbb #f5f8fa",
        }}
      >
        <DrilldownHeader
          navigate={navigate}
          toggleShowTotals={toggleShowTotals}
        />

        <ScreenContent
          padding={2}
          sx={{ pt: 0, overflowX: "auto", background: "#ffffff" }}
        >
          <OverviewDisplayFields />
        </ScreenContent>
        <DrilldownPrimaryNavigation baseUrl={baseUrl} />
        <PrimaryContentRouter baseUrl={baseUrl} />
      </Box>
      <LoanDrilldownScreenSidebar />
      <LoanSizerUploader
        show={type === loanDrilldownSidebarTypes.SIZER_UPLOAD}
        onClose={() => dispatch(sidebarClear())}
      />
    </Flx>
  );
});

/** =========================
 * DRILLDOWN HEADER
 * ========================= */
const DrilldownHeader = memo(({ navigate }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { getLoanById } = useUnderwritingHook();
  const loanId = loanDrilldown?.loanNumber;
  const [showRenameModal, setShowRenameModal] = useState(null);

  const handleReturnToPipeline = () => {
    dispatch(loanDrilldownClear());
    navigate("/loans");
  };

  const loanTitle = useMemo(() => {
    if (isNil(loanDrilldown?.loanName)) {
      return `Loan #${loanDrilldown?.loanNumber}`;
    }
    return loanDrilldown?.loanName || `Loan #${loanDrilldown?.loanNumber}`;
  }, [loanDrilldown]);

  const handleRefresh = () => {
    dispatch(loanDrilldownClear());
    getLoanById({
      loanId: loanDrilldown?._id,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
      },
      onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  return (
    <ScreenHeader
      titleTopContent={
        <Flx ac gap={1} sx={{ mb: 1.2 }}>
          <Link
            onClick={handleReturnToPipeline}
            sx={{
              fontSize: "12px",
              cursor: "pointer",
              fontFamily: "var(--ibm)",
            }}
          >
            Loan Pipeline
          </Link>
          <ChevronRight sx={{ fontSize: "12px" }} />
          <Link
            sx={{
              fontSize: "12px",
              cursor: "pointer",
              fontFamily: "var(--ibm)",
            }}
          >
            Loan {loanId}
          </Link>
        </Flx>
      }
      title={loanTitle}
      titleEndContent={
        <>
          <Tooltip title="Rename Loan Name">
            <IconButton
              sx={{ ml: 1 }}
              size="small"
              onClick={() => setShowRenameModal(loanDrilldown)}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh loan data">
            <IconButton sx={{ ml: 1 }} size="small" onClick={handleRefresh}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>

          <UpdateLoanDataModal
            loan={showRenameModal}
            field={"loanName"}
            inputLabel={"New Loan Name"}
            type="text"
            onClose={() => setShowRenameModal(null)}
            onSuccessFn={() => handleRefresh()}
          />
        </>
      }
      endContent={
        <Flx g={1}>
          <SendLoanApplicationsDropdownButton />
          <SalesforceIntegrationButton />
          <UploadToLoanDropdownButton />
          <AddToLoanDropdownButton />
        </Flx>
      }
    />
  );
});

/** =========================
 * NAVIGATION TABS
 * ========================= */
const DrilldownPrimaryNavigation = memo(({ baseUrl }) => {
  const theme = useTheme();

  const tabs = useMemo(
    () => [
      "Summary",
      "Loan Information",
      "Files",
      "Entity",
      "Borrowers",
      "Properties",
      "Sizer",
      "Appraisals",
      "Loan Exceptions",
      "Checklist",
    ],
    []
  );
  return (
    <Flx
      sx={{
        pl: 1,
        // background: "#f9f9fc",
        background: "#ffffff",
        borderTop: `1px solid #e2e8f0`,
        borderBottom: `1px solid #e2e8f0`,

        // borderTop: `1px solid ${theme.palette.divider}`,
        // borderBottom: `1px solid ${theme.palette.divider}`,
        overflowY: "auto",
      }}
    >
      <LoanDrilldownRouterTabSwitcher
        tabs={tabs}
        baseUrl={baseUrl}
        variant="background"
      />
    </Flx>
  );
});

/** =========================
 * DRILLDOWN CONTENT
 * ========================= */
const PrimaryContentRouter = memo(({ baseUrl }) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Routes>
        <Route path="/" element={<LoanDrilldownSummaryTab />} />
        <Route path="/summary" element={<LoanDrilldownSummaryTab />} />
        <Route path="/loan-information" element={<LoanDrilldownDetailsTab />} />
        <Route path="/files/*" element={<LoanDrilldownFilesTab />} />
        <Route path="/sizer" element={<LoanDrilldownSizerTab />} />
        <Route path="/borrowers" element={<LoanDrilldownBorrowersTab />} />
        <Route path="/borrowers" element={<LoanDrilldownBorrowersTab />} />
        <Route
          path="/borrowers/:borrowerId?"
          element={<LoanDrilldownBorrowersTab />}
        />

        <Route
          path="/properties/:propertyId?"
          element={<LoanDrilldownPropertiesTab />}
        />
        <Route path="/appraisals" element={<LoanDrilldownAppraisalsTab />} />
        <Route path="/entity" element={<LoanDrilldownEntityTab />} />
        <Route
          path="/loan-exceptions"
          element={<LoanDrilldownExceptionsTab />}
        />
        <Route path="/checklist" element={<LoanDrilldownChecklistTab />} />
        <Route path="*" element={<Navigate to={`${baseUrl}`} replace />} />
      </Routes>
    </Box>
  );
});

/** =========================
 * HELPER COMPONENTS
 * ========================= */
const OverviewDisplayFields = memo(() => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const borrowerNames = useMemo(() => {
    const borrowers = loanDrilldown?.borrowers;
    if (isEmpty(borrowers)) {
      return "None";
    }

    return borrowers
      ?.map((b) => {
        const { firstName, lastName } = b;
        return `${firstName} ${lastName}`;
      })
      .join(", ");
  }, [loanDrilldown?.borrowers]);

  const propertyNames = useMemo(() => {
    const properties = loanDrilldown?.subjectProperties;
    if (isEmpty(properties)) {
      return "None";
    }

    return properties
      ?.map((p) => {
        return `${isNil(p?.address?.fullAddress) ? "N/A" : p?.address?.fullAddress}`;
      })
      .join(", ");
  }, [loanDrilldown?.subjectProperties]);

  return (
    <RffLoanDataGroup
      sx={{
        display: "flex",
        gap: 3,
        mt: 1,
        ".MuiGrid-root": {
          // mr: 2,
          flexShrink: 0,
          // minWidth: "72px",
          ".MuiFormLabel-root , .MuiTypography-root": {
            whiteSpace: "nowrap",
          },
        },
      }}
      row
      // gap={1}
    >
      <RffDisplayField
        name="loanNumber"
        label="Loan #"
        type="select"
        data={loanDrilldown}
      />
      <RffDisplayField
        name={"loanStatus"}
        label={"Status"}
        type={"select"}
        data={loanDrilldown}
      />
      <Seperator />
      <RffDisplayField
        name="loanProductType"
        label="Product"
        // label="Loan Product Type"
        type="select"
        data={loanDrilldown}
      />
      <RffDisplayField
        name="loanPurpose"
        label="Purpose"
        // label="Loan Purpose"
        type="select"
        data={loanDrilldown}
      />
      <RffDisplayField
        name="totalLoanAmount"
        label="Total Loan Amount"
        type="dollar"
        data={loanDrilldown}
      />
      <Seperator />
      <RffDisplayField
        label="Entity Name"
        startContent={<AddAlertOutlined />}
        name="borrowerEntity.entityName"
        emptyValue="Not Set"
        maxWidth="140px"
        color={
          isNil(loanDrilldown?.borrowerEntity?.entityName) ? red[500] : null
        }
        icon={
          isNil(loanDrilldown?.borrowerEntity?.entityName) ? (
            <ErrorOutline className="thin" />
          ) : null
        }
        // value={
        //   isNil(loanDrilldown?.borrowerEntity?.entityName)
        //     ? "Not Set"
        //     : loanDrilldown?.borrowerEntity?.entityName
        // }
        // value={
        //   isNil(loanDrilldown?.borrowerEntity?.entityName)
        //     ? "Not Set"
        //     : loanDrilldown?.borrowerEntity?.entityName
        // }
        // maxWidth="200px"
      />
      <RffDisplayField
        label={
          size(loanDrilldown?.borrowers) > 1
            ? `Borrowers (${size(loanDrilldown?.borrowers)})`
            : "Borrower"
        }
        value={borrowerNames}
        maxWidth="140px"
      />
      <RffDisplayField
        label={
          size(loanDrilldown?.subjectProperties) > 1
            ? `Properties (${size(loanDrilldown?.subjectProperties)})`
            : "Property"
        }
        value={propertyNames}
        maxWidth="140px"
      />

      <Seperator />
      <PermittedUsersItem />
      {/* <RffDisplayField
        label={`Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
        value={permittedUsersNames}
        maxWidth="200px"
      /> */}
    </RffLoanDataGroup>
  );
});

const PermittedUsersItem = ({ children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { updateLoanPermittedUsers, loading } = useUnderwritingHook();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);

  const permittedUsersNames = useMemo(() => {
    const permittedUsers = loanDrilldown?.permittedUsers;
    if (isEmpty(permittedUsers)) {
      return "None";
    }

    return permittedUsers?.join(", ");
  }, [loanDrilldown?.permittedUsers]);

  const initialValues = useMemo(() => {
    return {
      permittedUsers: loanDrilldown?.permittedUsers || [],
    };
  }, [loanDrilldown?.permittedUsers]);

  const onSubmit = (values) => {
    const updatedPermittedUsers = values?.permittedUsers;
    console.log("userEmail", updatedPermittedUsers);
    updateLoanPermittedUsers({
      loanId: loanDrilldown?._id,
      permittedUsers: updatedPermittedUsers,
      onSuccessFn: (res) => {
        dispatch(loanDrilldownSet(res));
        setShow(false);
      },
    });
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Tooltip title="Add permitted users to view this loan">
          <IconButton
            sx={{
              position: "absolute",
              right: "30px",
              top: "-5px",
              zIndex: 1,
              color: "text.secondary",
            }}
            size="small"
            onClick={() => setShow(true)}
          >
            <AddRounded fontSize="small" />
          </IconButton>
        </Tooltip>

        <RffDisplayField
          label={`Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
          value={permittedUsersNames}
          maxWidth="200px"
        />
      </Box>

      <BasicModal
        show={show}
        onClose={() => setShow(false)}
        title="Permitted Users"
        maxWidth="sm"
      >
        <Flx column gap={1} sx={{ mt: 1, mb: 3 }}>
          <RffDisplayField
            label={`Current Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
            value={permittedUsersNames}
          />
        </Flx>
        <Divider sx={{ mb: 3 }} />
        <RffForm
          loading={loading}
          onSubmit={onSubmit}
          submitText="Add User"
          initialValues={initialValues}
        >
          <RffGroup>
            <RffSelectMultipleUserEmailField
              name="permittedUsers"
              displayFullLabel
              label="Select user to add to loan"
            />
          </RffGroup>
        </RffForm>
      </BasicModal>
    </>
  );
};

const Seperator = ({ children }) => {
  return (
    <Divider
      orientation="vertical"
      flexItem
      //
      // sx={{ ml: -1, mr: 2, my: 0.1 }}
    />
  );
};

export default LoanDrilldownScreen;
